import { useMemo } from 'react';

import {
  AccessTimeOutlined,
  EmailOutlined,
  TaskOutlined
} from '@mui/icons-material';
import { Skeleton, Stack, Typography } from '@mui/material';

import DashboardCard from '@/components/dashboardCard';
import TotalIcon from '@/components/icons/violationStatistics/TotalIcon';
import { useViolationsStatistics } from '@/hooks/queries/violations';
import { Violations } from '@/models/violations';
import { secondsToDaysHoursAndMinutes } from '@/utils/datesAndTime';

const safeValue = (value: number | undefined, fallback = 0) =>
  value ?? fallback;

const useDashboardCardItems = (violationStatistics?: Violations.Statistics) =>
  useMemo(() => {
    const {
      total,
      contacted,
      contactedUpdatedOrRemoved,
      updated,
      removed,
      closed,
      totalCreators,
      postToClosedAvgTime
    } = violationStatistics || {};
    const generatePercentage = (
      value: number | undefined,
      base: number | undefined = total
    ) =>
      value && base
        ? parseFloat(((safeValue(value) * 100) / base).toFixed(1))
        : 0;

    return [
      {
        icon: (
          <TotalIcon
            sx={{ color: '#fff', width: 36, height: 36 }}
            fontSize="small"
          />
        ),
        title: 'Total',
        value: safeValue(total),
        description: `By ${totalCreators} creators`
      },
      {
        icon: <EmailOutlined sx={{ color: '#fff', width: 36, height: 36 }} />,
        title: 'Contacted',
        value: safeValue(contacted),
        description: 'of violations have been followed up',
        descriptionPercentage: generatePercentage(contacted)
      },
      {
        icon: (
          <AccessTimeOutlined sx={{ color: '#fff', width: 36, height: 36 }} />
        ),
        title: 'Changed',
        value: safeValue(updated) + safeValue(removed),
        description: 'of followed-up posts were edited or removed',
        descriptionPercentage: generatePercentage(
          contactedUpdatedOrRemoved,
          contacted
        )
      },
      {
        icon: <TaskOutlined sx={{ color: '#fff', width: 36, height: 36 }} />,
        title: 'Closed',
        value: safeValue(closed),
        description: (
          <>
            Average time from Posting to Closing:{' '}
            <Typography
              component="p"
              variant="body2"
              fontWeight={500}
              color="text.primary"
            >
              {secondsToDaysHoursAndMinutes(postToClosedAvgTime || 0)}
            </Typography>
          </>
        )
      }
    ];
  }, [violationStatistics]);

const ViolationsStatistics = () => {
  const { data: violationStatistics, isLoading: isStatisticsLoading } =
    useViolationsStatistics({});

  const dashboardCardItems = useDashboardCardItems(violationStatistics);

  return (
    <>
      {isStatisticsLoading ? (
        <Stack
          direction="row"
          flexWrap="wrap"
          mt={1.5}
          gap={3}
          justifyContent="space-between"
        >
          {Array.from({ length: 4 }).map((_, idx) => (
            <Skeleton
              key={idx}
              sx={{
                flexGrow: 1,
                flexBasis: 0,
                minWidth: '314px'
              }}
              variant="rectangular"
              height={150}
            />
          ))}
        </Stack>
      ) : (
        <Stack
          direction="row"
          flexWrap="wrap"
          mt={1.5}
          gap={3}
          justifyContent="space-between"
        >
          {dashboardCardItems.map((item) => (
            <DashboardCard key={item.title} {...item} id="violation-card" />
          ))}
        </Stack>
      )}
    </>
  );
};

export default ViolationsStatistics;
